<template>
  <div>
    <!-- Keyed by attempt: every new error re-mounts its summary, which takes the focus. -->
    <UnresolvedLabelsSummary
      v-if="unresolvedLabels.length > 0"
      :key="`labels-${attempt}`"
      :labels="unresolvedLabels"
      class="upload-step__summary"
    />
    <ImportErrorAlert
      v-else-if="fileError"
      :key="`file-${attempt}`"
      :title="fileError"
      class="upload-step__summary"
    >
      <CopyToClipboardButton
        slot="actions"
        :text="fileError"
        :label="$t(`${I18N}.copyFileError`)"
      />
    </ImportErrorAlert>

    <!-- One centred column, read top to bottom: file, dates, action. -->
    <el-row
      type="flex"
      justify="center"
    >
      <el-col :span="12">
        <el-form label-position="top">
          <el-form-item>
            <!-- No `accept`: with it, el-upload drops a wrong dragged file
                 silently, and on-change could not say why. -->
            <el-upload
              class="upload-step__file"
              action=""
              drag
              :auto-upload="false"
              :file-list="fileList"
              :disabled="isPreparing"
              :on-change="handleFileChange"
              :on-remove="handleFileRemove"
            >
              <i class="el-icon-upload" />
              <div class="el-upload__text">
                {{ $t(`${I18N}.dropFile`) }}
                <br />
                <em>{{ $t(`${I18N}.clickToSelect`) }}</em>
              </div>
              <div
                slot="tip"
                class="el-upload__tip"
              >
                {{ $t(`${I18N}.fileTip`) }}
              </div>
            </el-upload>
          </el-form-item>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item
                :label="$t(`${I18N}.dateStart`)"
                for="import-values-date-start"
                required
              >
                <el-date-picker
                  id="import-values-date-start"
                  :value="value.dateStart"
                  type="date"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  :placeholder="$t(`${I18N}.datePlaceholder`)"
                  :disabled="isPreparing"
                  :picker-options="{ disabledDate: isFutureDate }"
                  @input="update({ dateStart: $event })"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item
                :label="$t(`${I18N}.dateEnd`)"
                for="import-values-date-end"
                required
              >
                <el-date-picker
                  id="import-values-date-end"
                  :value="value.dateEnd"
                  type="date"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  :placeholder="$t(`${I18N}.datePlaceholder`)"
                  :disabled="isPreparing || !value.dateStart"
                  :picker-options="{ disabledDate: isEndDateDisabled }"
                  @input="update({ dateEnd: $event })"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-button
            type="primary"
            icon="el-icon-document-checked"
            class="upload-step__action"
            :loading="isPreparing"
            :disabled="!canPrepare"
            @click="prepare"
          >
            {{ $t(`${I18N}.prepare`) }}
          </el-button>
        </el-form>
      </el-col>
    </el-row>
  </div>
</template>

<script>
  import { analysesDataApi } from '@/api';
  import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
  import ImportErrorAlert from './ImportErrorAlert.vue';
  import UnresolvedLabelsSummary from './UnresolvedLabelsSummary.vue';
  import {
    fileErrorIgnoredRows,
    fileErrorMessage,
    isBeforeDate,
    isCsvFileName,
    isFutureDate,
    serverErrorMessage,
  } from './wizardSteps';

  // Step 1 (FE-3, FE-8). Emits `input` with the updated file and dates,
  // `prepared` with the prepare response and `rejected` with the ignored rows
  // of a file prepare refused (FILE_ERROR, C60). The shell drops both on every
  // `input`: a summary never describes a file other than the selected one.
  export default {
    name: 'UploadStep',
    components: {
      CopyToClipboardButton,
      ImportErrorAlert,
      UnresolvedLabelsSummary,
    },
    props: {
      value: { type: Object, required: true },
      result: { type: Object, default: null },
    },
    data() {
      const { file } = this.value;
      return {
        I18N: 'analysesData.importValues.upload',
        fileList: file ? [{ name: file.name, raw: file }] : [],
        isPreparing: false,
        fileError: null,
        attempt: 0,
      };
    },
    computed: {
      unresolvedLabels() {
        return this.result ? this.result.unresolvedLabels : [];
      },
      canPrepare() {
        const { file, dateStart, dateEnd } = this.value;
        return Boolean(file && dateStart && dateEnd);
      },
    },
    methods: {
      isFutureDate,
      isEndDateDisabled(date) {
        return isFutureDate(date) || isBeforeDate(date, this.value.dateStart);
      },
      update(changes) {
        this.fileError = null;
        this.$emit('input', { ...this.value, ...changes });
      },
      handleFileChange(file) {
        if (!isCsvFileName(file.name)) {
          // A new array resets el-upload's own list to the previous file.
          this.fileList = [...this.fileList];
          this.notifyError(this.$t(`${this.I18N}.notCsv`));
          return;
        }
        this.fileList = [file];
        this.update({ file: file.raw });
      },
      handleFileRemove() {
        this.fileList = [];
        this.update({ file: null });
      },
      async prepare() {
        if (this.isPreparing) return;
        this.isPreparing = true;
        this.fileError = null;
        try {
          const { data } = await analysesDataApi.prepareImport(this.value);
          const count = data.unresolvedLabels.length;
          if (count > 0) {
            this.notifyError(
              this.$tc(`${this.I18N}.unresolvedLabels`, count, {
                file: this.value.file.name,
              })
            );
          }
          this.attempt += 1;
          this.$emit('prepared', data);
        } catch (error) {
          this.fileError = fileErrorMessage(error);
          this.attempt += 1;
          if (this.fileError) {
            this.$emit('rejected', fileErrorIgnoredRows(error));
          }
          this.notifyError(this.fileError || serverErrorMessage(error));
        } finally {
          this.isPreparing = false;
        }
      },
      notifyError(message) {
        this.$notify({ showClose: true, type: 'error', message });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .upload-step__summary {
    margin-bottom: 1rem;
  }

  // The one action of the step, as wide as the form it completes.
  .upload-step__action {
    width: 100%;
  }

  // theme-chalk fixes the dragger at 360px: it fills its column instead. The
  // form item's 40px line-height, meant for one-line controls, would space
  // the dragger text and the tip apart.
  .upload-step__file ::v-deep {
    .el-upload,
    .el-upload-dragger {
      display: block;
      width: 100%;
    }

    .el-upload__text,
    .el-upload__tip {
      line-height: 1.6;
    }
  }
</style>
